import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";
import { UtilityQuality } from "../../../domain/utilities";
import { resolveEnumDisplayValue } from "./enumDisplayResolver";

/**
 * ECSQL to read Utility Quality data for a node element.
 *
 * Maps from:
 *   CifSubsurfaceConflictAnalysis.BaseNode_HmiDataSetGeometryAspect
 *
 * to the UtilityQuality domain class.
 */
const ECSQL_UTILITY_QUALITY_BY_ELEMENT = `
  SELECT
    h.QualityLevel                         AS QualityLevel,
    h.InvestigationLevel                   AS InvestigationLevel,
    h.Authority                            AS Authority,
    h.HmiDataSetGeometry_AuthorityDescription AS AuthorityDescription
  FROM CifSubsurfaceConflictAnalysis.BaseNode_HmiDataSetGeometryAspect h
  WHERE h.Element.Id = ?
`;

/**
 * Query Utility Quality information for a single element.
 *
 * @param iModel    Open IModelConnection
 * @param elementId ECInstanceId of the node/element to query
 * @returns UtilityQuality instance or undefined if the aspect is missing
 */
export async function queryUtilityQualityForElement(
  iModel: IModelConnection,
  elementId: string,
): Promise<UtilityQuality | undefined> {
  const reader = iModel.createQueryReader(
    ECSQL_UTILITY_QUALITY_BY_ELEMENT,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
  );

  for await (const row of reader) {
    const plain = row.toRow() as any; // { QualityLevel, InvestigationLevel, Authority, AuthorityDescription }
    const decoded: any = { ...plain };
    decoded.QualityLevel = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.BaseNode_HmiDataSetGeometryAspect",
      "QualityLevel",
      plain.QualityLevel
    );
    decoded.InvestigationLevel = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.BaseNode_HmiDataSetGeometryAspect",
      "InvestigationLevel",
      plain.InvestigationLevel
    );
    decoded.Authority = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.BaseNode_HmiDataSetGeometryAspect",
      "Authority",
      plain.Authority
    );
    return UtilityQuality.fromObject(decoded);
  }

  // No BaseNode_HmiDataSetGeometryAspect for this element
  return undefined;
}
